/** Bulandi 2026 — Viral Voltage (seniors) event registration extras. */
export const BULANDI_VIRAL_VOLTAGE_EVENT_ID = "viral-voltage-seniors";

/** When true, new Viral Voltage sign-in is blocked on Events Sign In (existing rows stay read-only). */
export const BULANDI_VIRAL_VOLTAGE_SIGN_IN_DISABLED = true;

export const BULANDI_VIRAL_VOLTAGE_SIGN_IN_DISABLED_LABEL = "Registration closed";

export function isViralVoltageSignInDisabled() {
  return BULANDI_VIRAL_VOLTAGE_SIGN_IN_DISABLED;
}

export function isViralVoltageEventId(id) {
  return id === BULANDI_VIRAL_VOLTAGE_EVENT_ID;
}

const NEGATIVE_CELL = new Set([
  "no",
  "n",
  "false",
  "0",
  "-",
  "na",
  "n/a",
  "pending",
]);

const LEGACY_REGISTERED = new Set([
  "yes",
  "y",
  "true",
  "1",
  "✓",
  "√",
  "registered",
  "done",
  "ok",
  "x",
]);

/** @param {unknown} raw */
export function looksLikeInstagramProfileUrl(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return false;
  if (/^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9._]+\/?(\?.*)?$/i.test(s)) {
    return true;
  }
  const handle = s.replace(/^@/, "");
  return /^[A-Za-z0-9._]{1,30}$/.test(handle);
}

/** @param {unknown} raw @returns {string} normalized https URL or "" */
export function normalizeInstagramProfileUrl(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";
  s = s.replace(/\?.*$/, "").replace(/\/+$/, "");
  const urlMatch = /^(?:https?:\/\/)?(?:www\.)?instagram\.com\/([A-Za-z0-9._]+)/i.exec(s);
  if (urlMatch) {
    return `https://instagram.com/${urlMatch[1]}`;
  }
  const handle = s.replace(/^@/, "");
  if (/^[A-Za-z0-9._]{1,30}$/.test(handle)) {
    return `https://instagram.com/${handle}`;
  }
  return "";
}

/** @param {unknown} raw @returns {string | null} error message */
export function validateInstagramProfileUrl(raw) {
  const normalized = normalizeInstagramProfileUrl(raw);
  if (!normalized) {
    return "Enter your public Instagram profile link (e.g. https://instagram.com/yourname).";
  }
  if (!/^https:\/\/instagram\.com\/[A-Za-z0-9._]{1,30}$/.test(normalized)) {
    return "Enter a valid public Instagram profile URL.";
  }
  return null;
}

/** @param {unknown} val */
export function parseViralVoltageInstagramFromSheetCell(val) {
  const s = String(val ?? "").trim();
  if (!s || NEGATIVE_CELL.has(s.toLowerCase())) return "";
  if (looksLikeInstagramProfileUrl(s)) return normalizeInstagramProfileUrl(s);
  return "";
}

/** @param {unknown} val */
export function viralVoltageRegisteredInSheetCell(val) {
  const s = String(val ?? "").trim();
  if (!s || NEGATIVE_CELL.has(s.toLowerCase())) return false;
  if (parseViralVoltageInstagramFromSheetCell(s)) return true;
  return LEGACY_REGISTERED.has(s.toLowerCase());
}

/**
 * @param {object} p
 * @returns {{ error: string | null, viralVoltageInstagramUrl?: string }}
 */
export function buildViralVoltageRegistrationFields(p) {
  const { selectedIds, fromSheetIds, instagramUrl, lockedOnSheet } = p;
  const selected = selectedIds.has(BULANDI_VIRAL_VOLTAGE_EVENT_ID);
  const wasOnSheet = fromSheetIds.has(BULANDI_VIRAL_VOLTAGE_EVENT_ID);

  if (!selected) {
    if (wasOnSheet) {
      return { error: "Viral Voltage cannot be deselected once registered." };
    }
    return { error: null };
  }

  if (!wasOnSheet && isViralVoltageSignInDisabled()) {
    return {
      error: `Viral Voltage registration is closed on Events Sign In (${BULANDI_VIRAL_VOLTAGE_SIGN_IN_DISABLED_LABEL}).`,
    };
  }

  if (wasOnSheet && lockedOnSheet) {
    const normalized = normalizeInstagramProfileUrl(instagramUrl);
    if (normalized) {
      return { error: null, viralVoltageInstagramUrl: normalized };
    }
    return { error: null };
  }

  const err = validateInstagramProfileUrl(instagramUrl);
  if (err) return { error: err };
  return {
    error: null,
    viralVoltageInstagramUrl: normalizeInstagramProfileUrl(instagramUrl),
  };
}
